/**
 * Anonymous alert session token.
 *
 * Generates and persists a per-browser random session token used to scope
 * anonymous price alerts via RLS (header `x-anon-session`).
 *
 * GDPR: stored via safeLocalStorage to respect user consent. If the consent
 * for "preferences" is not granted, a volatile in-memory token is used so
 * anonymous alerts still work within the current page lifetime, but they
 * will not survive a reload.
 */

import { safeLocalStorage } from '@/utils/safeStorage';

const ANON_ALERT_SESSION_KEY = 'gildy_anon_alert_session';

let inMemoryToken: string | null = null;

const generateToken = (): string => {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 14) +
               Math.random().toString(36).substring(2, 14);
  return `anon_${ts}_${rand}`;
};

/**
 * Returns the current anon alert session token, creating one if missing.
 * Always returns a non-empty string of length >= 16.
 */
export const getAnonAlertSession = (): string => {
  if (typeof window === 'undefined') {
    // SSR: never used for RLS-scoped queries, return empty
    return '';
  }

  const existing = safeLocalStorage.getItem(ANON_ALERT_SESSION_KEY);
  if (existing && existing.length >= 16) {
    inMemoryToken = existing;
    return existing;
  }

  if (inMemoryToken && inMemoryToken.length >= 16) {
    return inMemoryToken;
  }

  const token = generateToken();
  inMemoryToken = token;
  // Best-effort persist (no-op without consent)
  safeLocalStorage.setItem(ANON_ALERT_SESSION_KEY, token);
  return token;
};

/**
 * Removes the persisted anon alert session token.
 * Call after migrating anonymous alerts to a freshly registered user.
 */
export const clearAnonAlertSession = (): void => {
  inMemoryToken = null;
  safeLocalStorage.removeItem(ANON_ALERT_SESSION_KEY);
};
